import {
  matchLanguage
} from "./chunk-EBUGNBEC.mjs";

// src/index.ts
import OtaClientFromLib from "@crowdin/ota-client";
import { atom } from "jotai";
import { loadable } from "jotai/utils";
import { createIntlCache as createIntlCacheFromLib, createIntl as createIntlFromLib } from "react-intl";

// src/safeCrowdin.ts
async function getContent(crowdin) {
  try {
    return await crowdin.getContent();
  } catch (e) {
    throw new Error("Error at crowdin.getContent", { cause: e });
  }
}
async function getFileTranslations(crowdin, file) {
  try {
    return await crowdin.getFileTranslations(file);
  } catch (e) {
    throw new Error(`Error at crowdin.getFileTranslations with file "${file}"`, { cause: e });
  }
}
async function getStringsByLocale(crowdin, languageCode) {
  try {
    return await crowdin.getStringsByLocale(languageCode);
  } catch (e) {
    throw new Error(`Error at crowdin.getStringsByLocale with languageCode "${languageCode}"`, { cause: e });
  }
}

// src/index.ts
function createIntlAtom({
  messages: messagesDefinitions,
  crowdinHash,
  memoizee,
  defaultLang,
  language = navigator.language,
  createIntlCache = createIntlCacheFromLib,
  createIntl = createIntlFromLib,
  createIntlOptions = {},
  OtaClient = OtaClientFromLib,
  crowdinOptions,
  findCrowdinFile,
  timeout,
  // eslint-disable-next-line no-console
  onError = console.error
}) {
  if (language === "en") {
    language = "en-GB";
  }
  const loadableWithMessage = (arg) => {
    const regularLoadable = loadable(arg);
    return atom((get) => {
      const got = get(regularLoadable);
      if (got.state === "hasError") {
        onError(got.error);
      }
      return got;
    });
  };
  const crowdin = new OtaClient(crowdinHash, {
    languageCode: language,
    ...crowdinOptions
  });
  const atomMemoized = memoizee((value) => atom(value));
  const loadableMemoized = memoizee(loadableWithMessage);
  const userLanguageAtom = atom(language);
  const getMessagesInAsync = memoizee((lang) => messagesDefinitions[lang]());
  const getCrowdinMemoized = memoizee(async (lang) => {
    if (findCrowdinFile) {
      const content = await getContent(crowdin);
      const foundFile = findCrowdinFile(content[lang]);
      if (foundFile) {
        return await getFileTranslations(crowdin, foundFile);
      }
    }
    return await getStringsByLocale(crowdin, lang);
  });
  const getTimeoutPromise = memoizee(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_lang) => new Promise((resolve) => {
      setTimeout(resolve, timeout);
    })
  );
  const intlAtom = atom((get) => {
    const userLanguage = get(userLanguageAtom);
    let fetchingBundle = false;
    let fetchingCrowdin = false;
    const messages = { ...messagesDefinitions[defaultLang] };
    const matchedInDefinitions = matchLanguage(userLanguage, Object.keys(messagesDefinitions));
    if (matchedInDefinitions !== defaultLang && matchedInDefinitions && matchedInDefinitions in messagesDefinitions) {
      const asyncMessagesState = get(
        loadableMemoized(
          atomMemoized(
            typeof messagesDefinitions[matchedInDefinitions] === "function" ? getMessagesInAsync(matchedInDefinitions) : messagesDefinitions[matchedInDefinitions]
          )
        )
      );
      if (asyncMessagesState.state === "loading") {
        fetchingBundle = true;
      } else if (asyncMessagesState.state === "hasData") {
        Object.assign(messages, asyncMessagesState.data);
      }
    }
    const crowdinState = get(loadableMemoized(atomMemoized(getCrowdinMemoized(matchedInDefinitions || defaultLang))));
    if (crowdinState.state === "loading") {
      fetchingCrowdin = true;
    } else if (crowdinState.state === "hasData") {
      Object.assign(messages, crowdinState.data);
    }
    let loading = fetchingBundle || fetchingCrowdin;
    if (loading) {
      const timeoutState = get(loadableMemoized(atomMemoized(getTimeoutPromise(matchedInDefinitions || defaultLang))));
      if (timeoutState.state !== "loading") {
        loading = false;
      }
    }
    const intlCache = createIntlCache();
    return Object.assign(
      createIntl(
        {
          locale: userLanguage,
          defaultLocale: userLanguage,
          messages,
          ...createIntlOptions
        },
        intlCache
      ),
      {
        /**
         * When true, it's loading the messages from the json file passed in messagesInAsync
         */
        fetchingBundle,
        /**
         * When true, it's loading the messages from Crowdin
         */
        fetchingCrowdin,
        /**
         * is `fetchingBundle || fetchingCrowdin` but if the timeout is reached, it will be false
         */
        loading,
        /**
         * the language that is being displayed based on languages you provided in `messages`
         * can be undefined if no match was found
         * @example
         * createIntlAtom({ defaultLang: 'en-GB', messages: { 'en-GB': ..., 'pt-BR': ..., it: ... }, language: 'pt' }) // matchedLanguage will be 'pt-BR'
         * createIntlAtom({ defaultLang: 'en-GB', messages: { 'en-GB': ..., 'pt-BR': ..., it: ... }, language: 'pt-PT' }) // matchedLanguage will be 'pt-BR'
         * createIntlAtom({ defaultLang: 'en-GB', messages: { 'en-GB': ..., 'pt-BR': ..., it: ... }, language: 'de' }) // matchedLanguage will be undefined
         */
        matchedLanguage: matchedInDefinitions,
        intlCache
      }
    );
  });
  return {
    intlAtom,
    /**
     * It's memoized
     * @example
     * getMessageInAsync('pt-BR').then((messages) => console.log(messages));
     * getMessageInAsync('pt-BR') === getMessageInAsync('pt-BR')
     */
    getMessagesInAsync,
    /**
     * It's memoized
     * @example
     * getCrowdinMemoized('pt-BR').then((messages) => console.log(messages));
     * getCrowdinMemoized('pt-BR') === getCrowdinMemoized('pt-BR')
     */
    getCrowdinMemoized,
    /**
     * @example
     * getDefaultStore().get(userLanguageAtom) === navigator.language
     * getDefaultStore().set(userLanguageAtom, 'en-GB') // changes the language
     */
    userLanguageAtom,
    crowdin
  };
}
export {
  createIntlAtom
};
//# sourceMappingURL=index.mjs.map